import React, { useMemo } from "react";
import styled, { css } from "styled-components";
import Gettext from "../common/Gettext";
import { fontSize } from "../common/styled-mixins";
import { Link as _Link } from "../common/Link";
import Money from "../Money";
import Timer, {
  getActive,
  getCompleted,
  getDuration
} from "../Tournaments/Timer";
import { ModalConsumer } from "../common/ModalProvider";
import { JoinPopup } from "../Tournaments/Popups";

import CloseIcon from "../Icons/Close";
import InviteImg from "../Tournaments/images/invite@3x.png";
import bgImg from "../../images/demo-tournament-bg.png";

const Wrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  z-index: 20003;
`;

const Overlay = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
`;

const Container = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translateX(-50%) translateY(-50%);
  width: 740px;
  color: #272b34;
  background-color: ${({ theme }) =>
    theme.mode === "light" ? "#F5F5F5" : "#272B34"};
  text-align: center;

  @media (max-width: 959px) {
    top: 0;
    left: 0;
    transform: none;
    display: flex;
    flex-direction: column;
    justify-content: center;
    width: 100%;
    height: 100%;
    border-radius: 0;
  }
  @media (max-height: 500px) {
    display: block;
    overflow-y: auto;
    position: relative;
    padding-top: 50px;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 19px;
  right: 19px;
  display: block;
  width: 20px;
  height: 20px;
  padding: 0;
  color: #717171;
  background: transparent;
  border: none;
  cursor: pointer;
`;

const Title = styled.div`
  font-size: ${fontSize(28)};
  line-height: 1.3;
  text-transform: uppercase;
  margin-bottom: 11px;
  font-weight: 700;
  color: ${({ theme }) => (theme.mode === "light" ? "#3B9478" : "#FFCC00")};
  @media (max-width: 768px) {
    font-size: ${fontSize(18)};
  }
`;

const SubTitle = styled.div`
  font-size: ${fontSize(14)};
  color: ${({ theme }) => (theme.mode === "light" ? "#919191" : "#FFFFFF")};
  margin-bottom: 42px;
  @media (max-width: 768px) {
    margin-bottom: 18px;
  }
`;

const Button = styled.button`
  position: relative;
  z-index: 1;
  background-color: #0085eb;
  color: #fff;
  font-size: ${fontSize(12)};
  padding: 11px 22px;
  border-radius: 19px;
  border: none;
  text-transform: uppercase;
  font-weight: 900;
  text-transform: uppercase;
  transition: 0.3s;
  cursor: pointer;
  :hover:not([disabled]) {
    background-color: #0468b5;
  }
  :disabled {
    opacity: 0.4;
  }
  @media (max-width: 768px) {
    margin-bottom: 20px;
  }
`;

const Body = styled.div`
  padding: 36px 0 40px;
  text-align: center;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const Details = styled(_Link)`
  color: ${({ theme }) => (theme.mode === "light" ? "#0085EB" : "#FFCC00")};
  font-size: ${fontSize(12)};
  text-transform: lowercase;
  ${props =>
    props.yellow &&
    css`
      color: #ffcc00;
    `}
`;

const FooterButtons = styled.div`
  position: relative;
  margin-right: 49px;
  margin-left: 49px;

  @media (max-width: 768px) {
    margin-left: 15px;
    margin-right: 15px;
  }
`;

const FooterLinks = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: absolute;
  top: 10px;
  left: 0;
  right: 0;
  width: 100%;
  @media (max-width: 768px) {
    position: relative;
    top: unset;
  }
`;

const TournamentBlock = styled.div`
  margin-bottom: 36px;
  background-color: #000;
  background-image: url(${bgImg});
  background-position: center;
  background-size: cover;
  background-repeat: no-repeat;
  position: relative;

  @media (max-width: 768px) {
    margin-bottom: 14px;
  }
`;
const TournamentContent = styled.div`
  text-align: center;
  padding: 23px;
  position: relative;
  @media (max-width: 768px) {
    padding: 18px;
  }
`;
const TournamentTitle = styled.div`
  color: #ffffff;
  margin-bottom: 22px;
  font-size: ${fontSize(28)};
  line-height: 1.2;
  font-weight: 700;
  @media (max-width: 768px) {
    font-size: ${fontSize(18)};
    margin-bottom: 16px;
  }
`;
const TournamentFund = styled.div`
  color: #ffcc00;
  margin-bottom: 22px;
  font-size: ${fontSize(28)};
  line-height: 1.2;
  font-weight: 700;
  @media (max-width: 768px) {
    font-size: ${fontSize(18)};
    margin-bottom: 16px;
  }
`;
const TournamentStartLabel = styled.span`
  color: #ffffff;
  font-size: ${fontSize(12)};
  line-height: 1.2;
`;
const TournamentStartTime = styled.span`
  color: #ffffff;
  font-size: ${fontSize(18)};
  line-height: 1.2;
  font-weight: 700;
`;
const TournamentSticker = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  width: 42px;
  height: 42px;
`;

function DemoTournamentPopup({ tournament, onDismiss }) {
  const memoizedInviteSticker = useMemo(
    () => <img src={InviteImg} alt="" />,
    []
  );

  return (
    <Wrapper>
      <Overlay onClick={onDismiss} />
      <Container>
        <CloseButton onClick={onDismiss}>
          <CloseIcon />
        </CloseButton>
        <Body>
          <Title>
            <Gettext>Congratulations</Gettext>
          </Title>

          <SubTitle>
            <Gettext>
              You have enough DEMO money to purchase a tournament invitation!
            </Gettext>
          </SubTitle>

          <TournamentBlock>
            <TournamentSticker>{memoizedInviteSticker}</TournamentSticker>
            <TournamentContent>
              <TournamentTitle>{tournament.title}</TournamentTitle>
              <TournamentFund>
                <Gettext>Prize fund</Gettext>:{" "}
                <Money>{tournament.prize_fund}</Money>
              </TournamentFund>
              <div>
                <Timer
                  days={getDuration(tournament, "days")}
                  hours={getDuration(tournament, "hours")}
                  minutes={getDuration(tournament, "minutes")}
                  seconds={getDuration(tournament, "seconds")}
                  active={getActive(tournament)}
                  isCompleted={getCompleted(tournament)}
                  labelOnTop={true}
                  flexDirection="column"
                  alignItems="center"
                  noIcon
                  TimeStyle={TournamentStartTime}
                  TextStyle={TournamentStartLabel}
                />
              </div>
            </TournamentContent>
          </TournamentBlock>
          <FooterButtons>
            <ModalConsumer>
              {({ showModal }) => (
                <Button
                  onClick={() => {
                    showModal(JoinPopup, tournament);
                    onDismiss();
                  }}
                >
                  {/* prettier-ignore */}
                  <Gettext>
                    Buy for {<Money>{tournament.participation_price}</Money>} demo money
                  </Gettext>
                </Button>
              )}
            </ModalConsumer>
            <FooterLinks>
              <Details to="/news/doubleup" target="_blank">
                <Gettext>Details</Gettext>
              </Details>
              <Details to="/tournaments" yellow>
                <Gettext>Other tournaments</Gettext>
              </Details>
            </FooterLinks>
          </FooterButtons>
        </Body>
      </Container>
    </Wrapper>
  );
}

export default DemoTournamentPopup;



// WEBPACK FOOTER //
// ./src/components/TradeModals/DemoTournamentPopup.js